/**
 * Search API Methods (V3)
 *
 * V3 search endpoints live under /hotpads-api/api/web/v3/search/* and use the
 * `HpApiResponse<T>` envelope. They share `apiClient` with V1/V2 — the `/web/v3`
 * path prefix selects the V3 backend (HP-7934 moved V3 under `/api/`).
 *
 * Types come from the prod-generated `@zg-rentals/ts-hotpads-api-client`, so
 * they track the BE contract automatically (no hand-authored spec to drift).
 */

import { INTERIM_ALL_RENTALS_SLUG } from '@/src/lib/seo/allRentalsSlug';
import type { PartialDeep } from 'type-fest';
import type {
  HpApiResponseDto,
  HpDeprecatedSlugEntryDto,
  HpListingCardDto,
  HpSearchListingsRequestDto,
  HpSearchListingsResponseDto,
  HpSearchScopeDto,
  HpSearchSlugRegistryDto,
  HpWebListingFilterDto,
} from '@zg-rentals/ts-hotpads-api-client';
import { apiClient, ApiError } from '../client/apiClient';

export type SearchSlugRegistry = HpSearchSlugRegistryDto;
export type DeprecatedSlugEntry = HpDeprecatedSlugEntryDto;

type SearchSlugsResponse = HpApiResponseDto<HpSearchSlugRegistryDto>;

/**
 * Fetch the V3 search slug registry: active slugs (with display + parent +
 * property-type label) and deprecated → redirect mappings. Throws ApiError if
 * the envelope's `error` field is populated or `data` is missing.
 */
export async function getSearchSlugs(): Promise<SearchSlugRegistry> {
  const response = await apiClient<SearchSlugsResponse>({
    path: '/web/v3/search/slugs',
  });
  if (response.error) {
    const error = new ApiError(response.error.message, { data: response });
    error.category = 'API_ENVELOPE_ERROR';
    throw error;
  }
  if (!response.data) {
    throw new ApiError('Empty data in /v3/search/slugs response', { data: response });
  }
  return response.data;
}

// ---------------------------------------------------------------------------
// V3 unified search — POST /web/v3/search/listings (replaces V2 byCoordsV2 for SRP)
// ---------------------------------------------------------------------------

/** Request body for {@link searchListings}. */
type SearchListingsFilter = PartialDeep<Omit<HpWebListingFilterDto, 'areaId' | 'scope'>> & {
  areaId?: string | number;
  scope?: HpSearchScopeDto;
};
export type SearchListingsRequest = PartialDeep<Omit<HpSearchListingsRequestDto, 'filters'>> & {
  filters?: SearchListingsFilter;
};
/** Resolved search payload: resolved slug/filters, canonical/share URLs, SEO, and listings. */
export type SearchListingsResult = HpSearchListingsResponseDto;
/** A single listing/building card within the results. */
export type SearchListingCard = HpListingCardDto;

type SearchListingsResponse = HpApiResponseDto<HpSearchListingsResponseDto>;

type BuildListingsSearchRequestInput = {
  area: SearchListingsRequest['area'];
  filters: SearchListingsFilter;
  limit: number;
  pageNumber?: number;
  searchSlug?: string;
};

/**
 * Builds a minimal V3 search request for non-SRP consumers. The BE validates
 * `area.resourceId`/`area.name`/`area.type` as required (400s without them —
 * verified against prod) and scopes results by `filters.boundingBox` /
 * `filters.areaId`; SRP requests (slug + area + boundary fitting) are built by
 * `buildSrpSearchRequest` instead.
 */
export function buildListingsSearchRequest({
  area,
  filters,
  limit,
  pageNumber = 1,
  searchSlug = INTERIM_ALL_RENTALS_SLUG,
}: BuildListingsSearchRequestInput): SearchListingsRequest {
  return {
    searchSlug,
    area,
    filters,
    page: { number: pageNumber, limit },
  };
}

/**
 * Fetch SRP results from the V3 unified search endpoint. The FE sends intent —
 * `searchSlug` + `area` + `filters` (the map viewport goes in
 * `filters.boundingBox`) — and the BE resolves the canonical slug, page
 * metadata, and the matching listings in one call. There is no response-shaping
 * lever (the `include` section dispatcher was removed in HP-8110); the only
 * knob is `includeBreadcrumbs`.
 *
 * Replaces the V2 `byCoordsV2` (`fetchByCoords`) call for SRP. Throws ApiError
 * if the envelope carries an error or `data` is missing.
 */
export async function searchListings(
  request: SearchListingsRequest,
  signal?: AbortSignal,
): Promise<SearchListingsResult> {
  const response = await apiClient<SearchListingsResponse>({
    path: '/web/v3/search/listings',
    method: 'POST',
    body: request,
    signal,
  });
  if (response.error) {
    const error = new ApiError(response.error.message, { data: response });
    error.category = 'API_ENVELOPE_ERROR';
    throw error;
  }
  if (!response.data) {
    throw new ApiError('Empty data in /web/v3/search/listings response', { data: response });
  }
  return response.data;
}

/**
 * Matching-unit total for `request` — the listing bodies are discarded, so send
 * `page.limit: 1`.
 *
 * Shared by the SRP's server prefetch and its client probe queries: both report
 * the same number for the same request, and the `numUnits ?? numBuildings`
 * fallback lives in one place.
 */
export async function fetchSearchUnitCount(request: SearchListingsRequest, signal?: AbortSignal): Promise<number> {
  const response = await searchListings(request, signal);
  const group = response.listings;
  return group?.numUnits ?? group?.numBuildingsAvailable ?? 0;
}
